import { ChangeDetectionStrategy, Component, input, computed } from '@angular/core';

let nextId = 0;

@Component({
    selector: 'app-progress-bar',
    templateUrl: './progress-bar.component.html',
    styleUrls: ['./progress-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: []
})
export class ProgressBarComponent {
  value = input<number>(0);
  max = input<number>(100);
  variant = input<'default' | 'text-inside'>('default');
  /** How far along cannot be told: the bar sweeps and reports no value. */
  indeterminate = input<boolean>(false);
  /**
   * Names the bar and exposes it as a progressbar with its value. A
   * text-inside bar is named by its text when this is null; a default bar
   * without a name stays presentational, for where nearby text reports the progress.
   */
  ariaLabel = input<string | null>(null);
  /** Spoken value instead of the percentage, e.g. "Uploading, step 2 of 3". */
  valueText = input<string | null>(null);

  readonly textId = `app-progress-bar-text-${nextId++}`;

  percentage = computed((): number => {
    const maxVal = this.max();
    if (!maxVal || maxVal === 0) {
      return 0;
    }
    const calculatedPercentage = (this.value() / maxVal) * 100;
    return Math.max(0, Math.min(Math.round(calculatedPercentage), 100));
  });

  readonly textInside = computed((): boolean => this.variant() === 'text-inside');

  /** Exposed as a progressbar: named by the label or, inside, by its own text. */
  readonly exposed = computed((): boolean => !!this.ariaLabel() || this.textInside());

  readonly labelledBy = computed((): string | null => (!this.ariaLabel() && this.textInside() ? this.textId : null));

  readonly ariaValueNow = computed((): number | null => (this.exposed() && !this.indeterminate() ? this.percentage() : null));

  /** Fill width as a fraction, for where the text changes colour. */
  readonly ratio = computed((): number => (this.indeterminate() ? 0 : this.percentage() / 100));
}
